import React from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes } from 'react';
import type { TabsProps } from './types';
import { TabsStyled } from './Tabs.styled';
import { TABS_BUTTON, TABS_CONTAINER, TABS_WRAPPER } from '../../util/data-id';
import type { NavLinkProps } from 'react-router-dom';
import { NavLink } from 'react-router-dom';

type Ref = HTMLDivElement;
type HTMLProps = HTMLAttributes<Ref>;
type Props = HTMLProps & TabsProps;
type TabsNavProps = {
  id: string;
  tabs: (NavLinkProps & { label: React.ReactNode | string })[];
};
const Tabs = React.forwardRef<Ref, Props>(({
  id,
  label,
  tabs,
  active,
  handleClick,
  ...other
}: Props, ref): JSX.Element => {
  return (
    <TabsStyled
      ref={ref}
      data-id={TABS_WRAPPER}
      role={'tablist'}
      aria-label={label}
      {...other as HTMLProps}
    >
      <div data-id={TABS_CONTAINER} className={'w-full flex items-center'}>
        {tabs.map((tab, index) => (
          <button
            key={`tab--${id}_${index}`}
            id={`tab--${id}_${index}`}
            role={'tab'}
            type={'button'}
            tabIndex={index}
            data-id={TABS_BUTTON}
            aria-controls={`tabpanel--${id}_${index}`}
            aria-selected={active === index ? 'true' : 'false'}
            onClick={() => handleClick(index)}
            {...tab.other as ButtonHTMLAttributes<HTMLButtonElement>}
          >
            {tab.content}
          </button>
        ))}
      </div>
    </TabsStyled>
  );
});

const TabsNav = React.forwardRef<Ref, TabsNavProps>(({
  id,
  tabs,
  ...other
}: TabsNavProps, ref): JSX.Element => {
  return (
    <TabsStyled
      ref={ref}
      data-id={TABS_WRAPPER}
      role={'tablist'}
      aria-label={'tabs'}
      {...other as HTMLProps}
    >
      <div data-id={TABS_CONTAINER} className={'w-full flex items-center'}>
        {tabs.map((tab, index) => (
          <NavLink
            key={`tab--${id}_${index}`}
            id={`tab--${id}_${index}`}
            role={'tab'}
            type={'button'}
            tabIndex={index}
            data-id={TABS_BUTTON}
            aria-controls={`tabpanel--${id}_${index}`}
            to={tab.to}
            activeClassName={'active'}
          >
            {tab.label}
          </NavLink>
        ))}
      </div>
    </TabsStyled>
  );
});

Tabs.displayName = 'Tabs';

export default Tabs;
export { TabsNav };
